import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { Loader2 } from 'lucide-react';
import { CanonicalTag } from '@/components/CanonicalTag';

export default function ResetPassword() {
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [isValidSession, setIsValidSession] = useState(false);
  const [checkingSession, setCheckingSession] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    // Check if we have a valid session for password reset
    const checkSession = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      
      if (session && session.user) {
        setIsValidSession(true);
        toast.success('You can now set your new password');
      } else {
        toast.error('Invalid or expired reset link');
        setTimeout(() => navigate('/auth'), 2000);
      }
      setCheckingSession(false);
    };

    checkSession();
  }, [navigate]);

  const handlePasswordUpdate = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (password !== confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }

    if (password.length < 12) {
      toast.error('Password must be at least 12 characters long');
      return;
    }

    setLoading(true);

    try {
      // Check session first
      const { data: { session } } = await supabase.auth.getSession();
      
      if (!session) {
        toast.error('Session expired. Please request a new reset link');
        navigate('/auth');
        return;
      }

      const { error } = await supabase.auth.updateUser({ 
        password: password 
      });

      if (error) {
        toast.error(error.message);
      } else {
        toast.success('Password updated successfully!');
        setTimeout(() => {
          navigate('/');
        }, 1500);
      }
    } catch (error: any) {
      toast.error(`An unexpected error occurred: ${error.message || 'Unknown error'}`);
    } finally {
      setLoading(false);
    }
  };

  if (checkingSession) {
    return (
      <div className="relative min-h-screen flex items-center justify-center">
        <div className="absolute inset-0">
          <img 
            src="/triathlon-finish-background.jpg"
            alt=""
            className="w-full h-full object-cover"
            loading="lazy"
          />
          <div className="absolute inset-0" style={{ backgroundColor: 'hsl(var(--background) / 0.80)' }} />
        </div>
        <Card className="relative w-full max-w-md shadow-modal">
          <CardContent className="flex items-center justify-center p-8">
            <Loader2 className="h-8 w-8 animate-spin" />
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!isValidSession) {
    return (
      <div className="relative min-h-screen flex items-center justify-center">
        <div className="absolute inset-0">
          <img 
            src="/triathlon-finish-background.jpg"
            alt=""
            className="w-full h-full object-cover"
            loading="lazy"
          />
          <div className="absolute inset-0" style={{ backgroundColor: 'hsl(var(--background) / 0.80)' }} />
        </div>
        <Card className="relative w-full max-w-md shadow-modal">
          <CardContent className="p-8 text-center">
            <p className="text-muted-foreground">Redirecting to login...</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="relative min-h-screen flex items-center justify-center">
      {/* Subtle branded background */}
      <div className="absolute inset-0">
        <img 
          src="/triathlon-finish-background.jpg"
          alt=""
          className="w-full h-full object-cover"
          loading="lazy"
        />
        <div className="absolute inset-0" style={{ backgroundColor: 'hsl(var(--background) / 0.80)' }}  />
      </div>
      
      <CanonicalTag url="/reset-password" />
      <Card className="relative w-full max-w-md shadow-modal">
        <CardHeader className="space-y-1 text-center">
          <CardTitle className="text-2xl font-light tracking-tight bg-gradient-to-r from-primary to-primary/70 bg-clip-text text-transparent">
            SINK BIKE RUN
          </CardTitle>
          <CardDescription>
            Set your new password for your triathlon companion
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handlePasswordUpdate} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="password">New Password</Label>
              <Input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                minLength={12}
                placeholder="Enter new password"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="confirmPassword">Confirm Password</Label>
              <Input
                id="confirmPassword"
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
                minLength={12}
                placeholder="Confirm new password"
              />
            </div>
            <Button 
              type="submit" 
              className="w-full" 
              disabled={loading}
            >
              {loading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Updating Password...
                </>
              ) : (
                'Update Password'
              )}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}